import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {
    AlertOutlined,
    AppstoreAddOutlined,
    BookOutlined,
    ApartmentOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    DeleteOutlined,
    DownloadOutlined,
    EditOutlined,
    FileSearchOutlined,
    LoadingOutlined,
    PlusOutlined,
    RollbackOutlined,
    SettingOutlined,
    SwapOutlined,
    TagsOutlined,
    UploadOutlined
} from "@ant-design/icons";
import {AutoComplete, Button, Form, Input, message, Modal, Popconfirm, Popover, Select, Space, Switch, Table, Tabs, Tag, Tooltip, Typography, Upload} from "antd";
import {Formik} from "formik";
import api from "../api";
import {AbilityContext} from '../helpers/Can';
import MetadataEntityTypeCreator, {canImportStandardSchema} from "../configurable_entities/MetadataEntityTypeCreator";
import MetadataEntityImportActions from "../configurable_entities/MetadataEntityImportActions";
import MetadataEntitySchemaOrder from "../configurable_entities/MetadataEntitySchemaOrder";
import TextAssetTypes from '../configurable_entities/TextAssetTypes';
import {handleApiError} from "../helpers/formatApiError";
import setTitle from "../helpers/setTitle";
import {useTranslation} from "react-i18next";
import EntityAssetFormFields from "../configurable_entities/EntityAssetFormFields";
import ConfigurableEntityIcon, {configurableIconLabel, configurableIconSelectOptions} from "../configurable_entities/ConfigurableEntityIcon";
import {entityAssetFormHasRequiredValues, entityTypeFields, normalizeEntityAssetFormValues} from "../configurable_entities/entityAssetFormPayload";
import AssetSpreadsheetImportSetup, {mappingLabel} from "../configurable_entities/AssetSpreadsheetImportSetup";
import MetadataEntityImportSetup, {metadataEntityMappingLabel, metadataEntityTypeFields} from "../configurable_entities/MetadataEntityImportSetup";
import {STANDARD_METADATA_FIELD_OPTIONS, standardMetadataFieldLabel} from "../configurable_entities/standardFields";
import User from "../helpers/User";
import OrgNavLink from "../helpers/OrgNavLink";
import TimeAgo from "../helpers/TimeAgo";
import useConsumer from "../../channels/consumer";
import AssetGroupChooser from "./AssetGroupChooser";
import useCurrentOrg from "../helpers/useCurrentOrg";
import {useLocation, useNavigate} from "../helpers/routerCompat";
import CustomMetaFields from "./CustomMetaFields";
import {subscribeToCustomMetaFieldsChanged} from "../helpers/customMetaFieldsEvents";
import {configurableEntityLoadPlan, configurableEntityPickerFields, configurableEntityTabKeys} from "../configurable_entities/configurableEntityAccess";

const {Text} = Typography;

const typeConfig = {
    entityType: {
        titleKey: "configurable-text-assets",
        title: "Text Asset Types",
        endpoint: "/api/entity_types",
        payloadKey: "entity_type",
        groupAttr: "entity_type_custom_meta_groups_attributes",
        fieldAttr: "entity_type_custom_meta_fields_attributes",
        groupIdKey: "custom_meta_group_id",
        fieldIdKey: "custom_meta_field_id"
    },
    metadataEntityType: {
        titleKey: "custom-metadata-entities",
        title: "Custom Metadata Entity",
        endpoint: "/api/metadata_entity_types",
        payloadKey: "metadata_entity_type",
        groupAttr: "metadata_entity_type_custom_meta_groups_attributes",
        fieldAttr: "metadata_entity_type_custom_meta_fields_attributes",
        groupIdKey: "custom_meta_group_id",
        fieldIdKey: "custom_meta_field_id"
    },
    group: {
        titleKey: "custom-meta-groups",
        title: "Field Groups",
        endpoint: "/api/custom_meta_groups",
        payloadKey: "custom_meta_group",
        fieldAttr: "custom_meta_group_memberships_attributes",
        fieldIdKey: "custom_meta_field_id"
    },
    assetImport: {
        endpoint: "/api/asset_spreadsheet_imports"
    },
    metadataImport: {
        endpoint: "/api/metadata_entity_imports"
    }
};

const STANDARD_TAG_SCHEMA_DEFAULTS = [
    {standard_tag_sub_type: "keyword", name: "Keywords", slug: "standard-keywords", icon: "classification", description: "Custom fields attached to standard keyword tags."},
    {standard_tag_sub_type: "person", name: "People", slug: "standard-people", icon: "team", description: "Custom fields attached to standard person tags."},
    {standard_tag_sub_type: "event", name: "Events", slug: "standard-events", icon: "calendar", description: "Custom fields attached to standard event tags."},
    {standard_tag_sub_type: "product", name: "Products", slug: "standard-products", icon: "project", description: "Custom fields attached to standard product tags."},
    {standard_tag_sub_type: "artwork", name: "Artwork", slug: "standard-artwork", icon: "classification", description: "Custom fields attached to standard artwork tags."}
];

const STANDARD_TAG_TAXONOMY_HASHES = {
    keyword: "browse-keywords",
    person: "browse-people",
    event: "browse-events",
    product: "browse-products",
    artwork: "browse-artwork"
};

export const standardTagSchemaRows = (metadataEntityTypes, standardTagCounts) => STANDARD_TAG_SCHEMA_DEFAULTS.map(defaults => {
    const configured = metadataEntityTypes.find(type => type.standard_tag_sub_type === defaults.standard_tag_sub_type);
    return configured ? {
        ...configured,
        tag_count: standardTagCounts?.[defaults.standard_tag_sub_type] ?? configured.tag_count,
    } : {
        ...defaults,
        enabled: true,
        custom_meta_groups: [],
        custom_meta_fields: [],
        standard_field_keys: [],
        tag_fields: [
            {key: "name", label: "Name"}, {key: "list", label: "List"},
            {key: "description", label: "Description"}, {key: "link", label: "Link"},
            {key: "poster_image_guid", label: "Poster Image GUID"},
            ...(defaults.standard_tag_sub_type === "event" ? [
                {key: "date_start", label: "Date Start"}, {key: "date_end", label: "Date End"},
                {key: "location_country", label: "Country"}, {key: "location_country_code", label: "Country Code"},
                {key: "location_state", label: "State"}, {key: "location_city", label: "City"},
                {key: "location_name", label: "Location Name"}, {key: "featured_organization_name", label: "Featured Organization"}
            ] : []),
            ...(defaults.standard_tag_sub_type === "product" ? [{key: "sku", label: "SKU"}, {key: "gtin", label: "GTIN"}, {key: "featured_organization_name", label: "Featured Organization"}] : []),
            ...(defaults.standard_tag_sub_type === "artwork" ? [
                {key: "artwork_title", label: "Artwork Title"}, {key: "artwork_creator", label: "Artwork Creator"},
                {key: "artwork_date_created", label: "Artwork Date Created"}, {key: "artwork_medium", label: "Artwork Medium"},
                {key: "artwork_style_period", label: "Artwork Style/Period"}, {key: "artwork_source", label: "Artwork Source"},
                {key: "artwork_inventory_number", label: "Artwork Inventory Number"}, {key: "artwork_copyright_notice", label: "Artwork Copyright Notice"},
                {key: "artwork_circa_date_created", label: "Artwork Circa Date Created"},
                {key: "artwork_content_description", label: "Artwork Content Description"},
                {key: "artwork_contribution_description", label: "Artwork Contribution Description"},
                {key: "artwork_creator_id", label: "Artwork Creator ID"}, {key: "artwork_copyright_owner_id", label: "Artwork Copyright Owner ID"},
                {key: "artwork_licensor_id", label: "Artwork Licensor ID"}, {key: "artwork_licensor_name", label: "Artwork Licensor Name"},
                {key: "artwork_physical_description", label: "Artwork Physical Description"},
                {key: "artwork_source_inventory_url", label: "Artwork Source Inventory URL"},
                {key: "collection_management_system", label: "Collection Management System"}
            ] : [])
        ],
        tag_count: standardTagCounts?.[defaults.standard_tag_sub_type],
        standard_tag_schema: true
    };
});

export default function ConfigurableEntities({mode = "text"}) {
    const {t} = useTranslation();
    const currentOrg = useCurrentOrg();
    const location = useLocation();
    const navigate = useNavigate();
    const ability = useContext(AbilityContext);
    const canEditTypes = ability.can("create", "EntityType");
    const canManageGroups = ability.can("manage", "CustomMetaGroup");
    const metadataMode = mode === "metadata";
    const metadataEntitiesEnabled = !!(currentOrg?.configurable_entities_enabled && currentOrg?.metadata_entities_enabled);
    const canManageCustomMetaFields = ability.can("manage", "CustomMetaField");
    const canManageMetadataEntityTypes = ability.can("manage", "MetadataEntityType");
    const canManageCustomMetaGroups = ability.can("manage", "CustomMetaGroup");
    const canManageMetadataEntityImports = ability.can("manage", "MetadataEntityImport");
    const tabKeys = configurableEntityTabKeys({
        metadataMode,
        metadataEntitiesEnabled,
        canManageCustomMetaFields,
        canManageMetadataEntityTypes,
        canManageCustomMetaGroups,
        canManageMetadataEntityImports
    });
    const defaultTab = tabKeys[0];
    setTitle(metadataMode ? t("custom-metadata", "Custom Metadata") : t("text-asset-types", "Text Asset Types"));

    const tabFromHash = (locationHash = location.hash) => {
        const hash = locationHash.replace("#", "");
        const normalized = metadataMode && hash === "standard-metadata-entities" ? "metadata-entities" : hash;
        if (!tabKeys.includes(normalized)) return defaultTab;
        return metadataMode && !metadataEntitiesEnabled ? defaultTab : normalized;
    };

    const [tab, setTab] = useState(() => tabFromHash(location.hash));

    useEffect(() => {
        if (metadataMode || !['#metadata-entities', '#metadata-entity-fields', '#metadata-entity-imports', '#standard-metadata-entities'].includes(location.hash)) return;
        navigate(`${location.pathname.replace(/\/configurable-entities$/, '/custom-metadata-entities')}${location.hash}`, {replace: true});
    }, [location.pathname, location.hash, metadataMode, navigate]);

    const onTabClick = key => {
        setTab(key);
        window.location.hash = key;
    };

    useEffect(() => {
        setTab(tabFromHash(location.hash));
    }, [location.hash, metadataEntitiesEnabled, metadataMode, canManageCustomMetaFields, canManageMetadataEntityTypes, canManageCustomMetaGroups, canManageMetadataEntityImports]);

    const consumer = useConsumer();

    useEffect(() => {
        if (!consumer) return;

        const subscription = consumer.subscriptions.create({channel: "UserChannel"}, {
            received: data => {
                if (data.type === "assetSpreadsheetImport" && data.attrs?.id) {
                    setAssetImports(prev => prev.map(assetImport => (
                        assetImport.id === data.attrs.id ? {...assetImport, ...data.attrs} : assetImport
                    )));
                }
                if (data.type === "metadataEntityImport" && data.attrs?.id) {
                    setMetadataEntityImports(prev => prev.map(metadataImport => (
                        metadataImport.id === data.attrs.id ? {...metadataImport, ...data.attrs} : metadataImport
                    )));
                }
            }
        });

        return () => subscription.unsubscribe();
    }, [consumer]);

    const [entityTypes, setEntityTypes] = useState([]);
    const [metadataEntityTypes, setMetadataEntityTypes] = useState([]);
    const [standardTagCounts, setStandardTagCounts] = useState(null);
    const [groups, setGroups] = useState([]);
    const [assetImports, setAssetImports] = useState([]);
    const [metadataEntityImports, setMetadataEntityImports] = useState([]);
    const [assetFields, setAssetFields] = useState([]);
    const [conversionFields, setConversionFields] = useState([]);
    const [loading, setLoading] = useState(false);
    const loadGeneration = useRef(0);
    const currentLoadContext = useRef(null);
    currentLoadContext.current = {organizationId: currentOrg?.id, metadataMode};
    const [modalOpen, setModalOpen] = useState(false);
    const [savingEditor, setSavingEditor] = useState(false);
    const [assetModalOpen, setAssetModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [creatingEntityType, setCreatingEntityType] = useState(null);
    const [creatingAsset, setCreatingAsset] = useState(false);
    const [assetFormSubmittable, setAssetFormSubmittable] = useState(false);
    const [form] = Form.useForm();
    const [assetForm] = Form.useForm();
    const [conversionForm] = Form.useForm();
    const [conversionModalOpen, setConversionModalOpen] = useState(false);
    const [convertingMetadataEntityType, setConvertingMetadataEntityType] = useState(null);
    const [converting, setConverting] = useState(false);

    const selectableFields = useMemo(() => {
        return configurableEntityPickerFields({assetFields, conversionFields, metadataMode, editing, entityTypes});
    }, [assetFields, conversionFields, metadataMode, editing, entityTypes]);

    const fieldOptions = useMemo(() => {
        return selectableFields.map(field => ({
            value: field.id,
            label: `${field.name} (${field.owner_type}, ${field.value_type}${field.free ? "" : ", controlled"})`
        }));
    }, [selectableFields]);

    const standardTagSchemas = standardTagSchemaRows(metadataEntityTypes, standardTagCounts);
    const customMetadataEntityTypes = metadataEntityTypes.filter(type => !type.standard_tag_schema);
    const importableMetadataEntityTypes = metadataEntityTypes.filter(type => type.id && type.enabled);

    const groupOptions = useMemo(() => (
        groups.map(group => ({
            value: group.id,
            label: group.name
        }))
    ), [groups]);

    const loadData = () => {
        if (currentLoadContext.current.organizationId !== currentOrg?.id || currentLoadContext.current.metadataMode !== metadataMode) return;
        const generation = ++loadGeneration.current;
        setStandardTagCounts(null);
        setLoading(true);
        const plan = configurableEntityLoadPlan({
            metadataMode,
            metadataEntitiesEnabled,
            canManageMetadataEntityTypes,
            canManageCustomMetaFields,
            canManageCustomMetaGroups,
            canManageMetadataEntityImports
        });
        const emptyResponse = () => Promise.resolve({data: []});
        Promise.all([
            plan.entityTypes ? api.get("/api/entity_types") : emptyResponse(),
            plan.metadataEntityTypes ? api.get("/api/metadata_entity_types?include_standard_tag_schemas=true") : emptyResponse(),
            plan.groups ? api.get("/api/custom_meta_groups") : emptyResponse(),
            plan.assetImports ? api.get("/api/asset_spreadsheet_imports") : emptyResponse(),
            plan.metadataEntityImports ? api.get("/api/metadata_entity_imports") : emptyResponse(),
            plan.schemaFields ? api.get(`/api/custom_meta_fields?owner_type=${metadataMode ? "MetadataEntity" : "Asset"}`) : emptyResponse(),
            plan.conversionFields ? api.get("/api/custom_meta_fields?owner_type=Asset") : emptyResponse(),
            metadataMode && metadataEntitiesEnabled && canManageMetadataEntityTypes && tab === "metadata-entities"
                ? api.get("/api/metadata_entity_types/standard_tag_counts").catch(error => {
                    handleApiError(error);
                    return {data: null};
                })
                : Promise.resolve({data: null})
        ]).then(([entityRes, metadataRes, groupRes, importRes, metadataImportRes, assetFieldRes, conversionFieldRes, standardCountRes]) => {
            if (generation !== loadGeneration.current) return;
            setEntityTypes(entityRes.data);
            setMetadataEntityTypes(metadataRes.data);
            setGroups(groupRes.data);
            setAssetImports(importRes.data);
            setMetadataEntityImports(metadataImportRes.data);
            setAssetFields(assetFieldRes.data);
            setConversionFields(conversionFieldRes.data);
            setStandardTagCounts(standardCountRes.data);
            setLoading(false);
        }).catch(error => {
            if (generation !== loadGeneration.current) return;
            setLoading(false);
            handleApiError(error);
        });
    };

    const loadDataRef = useRef(loadData);
    loadDataRef.current = loadData;

    useEffect(() => {
        if (!metadataMode || !metadataEntitiesEnabled || !canManageCustomMetaFields) return;
        const organizationId = currentOrg?.id;
        return subscribeToCustomMetaFieldsChanged({ownerType: "MetadataEntity", organizationId, onChange: () => {
            if (currentLoadContext.current.organizationId !== organizationId || !currentLoadContext.current.metadataMode) return;
            loadDataRef.current();
        }});
    }, [currentOrg?.id, metadataMode, metadataEntitiesEnabled, canManageCustomMetaFields]);

    useEffect(() => {
        loadData();
        return () => { loadGeneration.current++; };
    }, [currentOrg?.id, metadataEntitiesEnabled, metadataMode, canManageCustomMetaFields, canManageMetadataEntityTypes, canManageCustomMetaGroups, canManageMetadataEntityImports, tab]);

    const conversionPending = convertingMetadataEntityType?.cmf_conversions?.some(conversion => conversion.status === "pending");

    useEffect(() => {
        if (!conversionModalOpen || !convertingMetadataEntityType?.id || !conversionPending) return;

        let active = true;
        const refreshConversion = () => {
            api.get(`${typeConfig.metadataEntityType.endpoint}/${convertingMetadataEntityType.id}`).then(res => {
                if (active) setConvertingMetadataEntityType(res.data);
            }).catch(() => {});
        };
        const interval = window.setInterval(refreshConversion, 2000);
        return () => {
            active = false;
            window.clearInterval(interval);
        };
    }, [conversionModalOpen, convertingMetadataEntityType?.id, conversionPending]);

    const openEditor = (kind, record = null) => {
        setEditing({kind, record});
        setModalOpen(true);
        form.setFieldsValue({
            name: record?.name || "",
            slug: record?.slug || "",
            icon: record?.icon || "",
            description: record?.description || "",
            enabled: record?.enabled ?? true,
            main_text_editable: record?.main_text_editable ?? record?.display_config?.main_text_editable ?? true,
            group_ids: record?.custom_meta_groups?.map(membership => membership.custom_meta_group.id) || [],
            field_ids: record?.custom_meta_fields?.map(membership => membership.custom_meta_field.id) || [],
            standard_field_keys: record?.standard_field_keys || record?.table_config?.standard_field_keys || [],
            preset: record?.preset || "blank"
        });
    };

    const closeEditor = () => {
        setModalOpen(false);
        setSavingEditor(false);
        setEditing(null);
        form.resetFields();
    };

    const openAssetCreator = entityType => {
        setCreatingEntityType(entityType);
        setCreatingAsset(false);
        setAssetFormSubmittable(false);
        setAssetModalOpen(true);
        assetForm.resetFields();
        assetForm.setFieldsValue({filename: "", content: "", custom_meta: {}});
    };

    const closeAssetCreator = () => {
        setAssetModalOpen(false);
        setCreatingEntityType(null);
        setCreatingAsset(false);
        setAssetFormSubmittable(false);
        assetForm.resetFields();
    };

    const saveEditor = () => {
        form.validateFields().then(values => {
            setSavingEditor(true);
            const {kind, record} = editing;
            const config = typeConfig[kind];
            const payload = buildPayload(kind, record, values);
            const request = record?.id ?
                api.put(`${config.endpoint}/${record.id}`, {[config.payloadKey]: payload}) :
                api.post(config.endpoint, {[config.payloadKey]: payload});

            request.then(() => {
                message.success(t("message-configuration-saved", "Configuration saved."));
                closeEditor();
                loadData();
            }).catch(error => {
                setSavingEditor(false);
                handleApiError(error);
            });
        });
    };

    const destroyRecord = (kind, record) => {
        const config = typeConfig[kind];
        api.delete(`${config.endpoint}/${record.id}`).then(() => {
            message.success(t("message-configuration-removed", "Configuration removed."));
            loadData();
        }).catch(handleApiError);
    };

    const saveAssetCreator = () => {
        assetForm.validateFields().then(values => {
            setCreatingAsset(true);
            api.post(`${typeConfig.entityType.endpoint}/${creatingEntityType.id}/assets`, {
                asset: normalizeEntityAssetFormValues(values, creatingEntityType)
            }).then(() => {
                message.success(t("configurable-text-asset-created", "Text asset created."));
                closeAssetCreator();
                loadData();
            }).catch(error => {
                setCreatingAsset(false);
                handleApiError(error);
            });
        });
    };

    const updateAssetCreatorSubmittable = () => {
        setAssetFormSubmittable(entityAssetFormHasRequiredValues(assetForm.getFieldsValue(true), creatingEntityType));
    };

    const buildPayload = (kind, record, values) => {
        const payload = {
            name: values.name,
            description: values.description,
            icon: values.icon,
            enabled: values.enabled
        };

        if (kind !== "group") {
            const config = typeConfig[kind];
            if (values.slug?.trim()) payload.slug = values.slug.trim();
            if (kind === "entityType") {
                payload.kind = "configurable_text_asset";
                payload.display_config = {
                    ...(record?.display_config || {}),
                    main_text_editable: values.main_text_editable !== false
                };
            }
            if (kind === "metadataEntityType") {
                payload.standard_field_keys = record?.standard_tag_sub_type ? [] : values.standard_field_keys || [];
                if (record?.standard_tag_sub_type) payload.standard_tag_sub_type = record.standard_tag_sub_type;
                if (!record?.id && !record?.standard_tag_sub_type) payload.preset = "blank";
            }
            payload[config.groupAttr] = nestedMemberships(
                record?.custom_meta_groups || [],
                values.group_ids || [],
                config.groupIdKey,
                membership => membership.custom_meta_group.id
            );
            payload[config.fieldAttr] = nestedMemberships(
                record?.custom_meta_fields || [],
                values.field_ids || [],
                config.fieldIdKey,
                membership => membership.custom_meta_field.id
            );
        } else {
            payload.custom_meta_group_memberships_attributes = nestedMemberships(
                record?.custom_meta_fields || [],
                values.field_ids || [],
                typeConfig.group.fieldIdKey,
                membership => membership.custom_meta_field.id
            );
        }

        return payload;
    };

    const exportMetadataEntityType = record => {
        api.get(`${typeConfig.metadataEntityType.endpoint}/${record.id}/export`, {responseType: "blob"}).then(res => {
            const url = URL.createObjectURL(res.data);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${record.slug}-custom-metadata-entities.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }).catch(handleApiError);
    };

    const openConversionModal = record => {
        setConvertingMetadataEntityType(record);
        setConversionModalOpen(true);
    };

    const closeConversionModal = () => {
        setConversionModalOpen(false);
        setConvertingMetadataEntityType(null);
        setConverting(false);
    };

    const convertCustomMetaField = () => {
        conversionForm.validateFields().then(values => {
            setConverting(true);
            api.post(
                `${typeConfig.metadataEntityType.endpoint}/${convertingMetadataEntityType.id}/convert_custom_meta_field`,
                {custom_meta_field_id: values.custom_meta_field_id}
            ).then(res => {
                setConverting(false);
                setConvertingMetadataEntityType(res.data);
                const pending = res.data?.cmf_conversions?.some(conversion => conversion.status === "pending");
                message.success(pending ?
                    t("cmf-conversion-queued", "Custom metadata field conversion queued.") :
                    t("cmf-conversion-complete", "Custom metadata field converted."));
                loadData();
                conversionForm.resetFields();
            }).catch(error => {
                setConverting(false);
                handleApiError(error);
            });
        });
    };

    const rollbackCustomMetaFieldConversion = conversion => {
        api.post(
            `${typeConfig.metadataEntityType.endpoint}/${convertingMetadataEntityType.id}/rollback_custom_meta_field_conversion`,
            {conversion_id: conversion.id}
        ).then(res => {
            setConvertingMetadataEntityType(res.data);
            message.success(t("cmf-conversion-rolled-back", "CMF conversion rolled back."));
            loadData();
        }).catch(handleApiError);
    };

    const schemaCustomFields = (record, ownerType) => {
        const direct = (record.custom_meta_fields || []).map(membership => membership.custom_meta_field);
        const grouped = (record.custom_meta_groups || []).flatMap(membership =>
            (membership.custom_meta_group?.custom_meta_fields || []).map(entry => entry.custom_meta_field));
        return [...new Map([...direct, ...grouped].filter(field => field?.owner_type === ownerType).map(field => [field.id, field])).values()];
    };

    const schemaColumns = (kind) => [
        {
            title: t("name", "Name"),
            dataIndex: "name",
            width: 220,
            render: (name, record) => (
                <Space direction="vertical" size={0} style={{maxWidth: 200}}>
                    <Text strong>{name}</Text>
                    {record.description && (
                        <Text type="secondary" ellipsis={{tooltip: record.description}} style={{maxWidth: 200}}>
                            {record.description}
                        </Text>
                    )}
                </Space>
            )
        },
        {title: t("slug", "Slug"), dataIndex: "slug", width: 140},
        {
            title: t("icon", "Icon"),
            dataIndex: "icon",
            width: 120,
            render: (icon, record) => icon ? (
                <Tag>
                    <ConfigurableEntityIcon icon={icon} kind={kind === "metadataEntityType" ? "metadata_entity" : "text_asset"}/>
                    {configurableIconLabel(icon)}
                </Tag>
            ) : null
        },
        {
            title: t("enabled", "Enabled"),
            dataIndex: "enabled",
            width: 80,
            render: enabled => enabled ? t("yes", "Yes") : t("no", "No")
        },
        kind === "entityType" && {
            title: t("main-text", "Main Text"),
            dataIndex: "main_text_editable",
            width: 110,
            render: editable => editable === false ? t("locked", "Locked") : t("editable", "Editable")
        },
        {
            title: t("groups", "Groups"),
            dataIndex: "custom_meta_groups",
            width: 160,
            render: groups => (
                <Space wrap size={[4, 4]}>
                    {groups?.map(membership => (
                        <GroupTag key={membership.id} membership={membership}/>
                    ))}
                </Space>
            )
        },
        kind === "entityType" && {
            title: t("direct-fields", "Direct Fields"),
            dataIndex: "custom_meta_fields",
            width: 110,
            render: fields => fields?.length || 0
        },
        kind === "metadataEntityType" && {
            title: t("asset-fields", "Asset Fields"),
            width: 230,
            render: (_, record) => <Space wrap size={[4, 4]}>
                {(record.standard_field_keys || []).map(key => <Tag key={`standard:${key}`}>{standardMetadataFieldLabel(key)}</Tag>)}
                {schemaCustomFields(record, "Asset").map(field => <Tag key={`custom:${field.id}`}>{field.name}</Tag>)}
            </Space>
        },
        kind === "metadataEntityType" && {
            title: t("entity-fields", "Entity Fields"),
            width: 230,
            render: (_, record) => <Space wrap size={[4, 4]}>
                {schemaCustomFields(record, "MetadataEntity").map(field => <Tag key={`custom:${field.id}`}>{field.name}</Tag>)}
            </Space>
        },
        kind === "metadataEntityType" && {
            title: t("built-in-properties", "Built-in Properties"),
            width: 200,
            render: (_, record) => <Space wrap size={[4, 4]}>
                {(record.tag_fields || []).filter(field => !["taxonomy_path", "cms_id"].includes(field.key)).map(field => <Tag key={field.key}>{field.label}</Tag>)}
            </Space>
        },
        kind === "entityType" && {
            title: t("assets", "Assets"),
            dataIndex: "asset_count",
            width: 70,
            render: (assetCount, record) => (
                <OrgNavLink to={`/explore?entity_type_id=${record.id}`}>
                    {assetCount || 0}
                </OrgNavLink>
            )
        },
        kind === "metadataEntityType" && {
            title: t("entities", "Entities"),
            dataIndex: "tag_count",
            width: 70,
            render: (tagCount, record) => (
                <OrgNavLink to={`/manage/taxonomy#${record.standard_tag_sub_type ? STANDARD_TAG_TAXONOMY_HASHES[record.standard_tag_sub_type] : `browse-metadata-entity-${record.id}`}`}>
                    {tagCount ?? "—"}
                </OrgNavLink>
            )
        },
        actionColumn(kind)
    ].filter(Boolean);

    const groupColumns = [
        {
            title: t("name", "Name"),
            dataIndex: "name",
            sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
            render: (name, record) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{name}</Text>
                    {record.description && <Text type="secondary">{record.description}</Text>}
                </Space>
            )
        },
        {
            title: t("icon", "Icon"),
            dataIndex: "icon",
            width: 120,
            render: icon => icon ? (
                <Tag>
                    <ConfigurableEntityIcon icon={icon}/>
                    {configurableIconLabel(icon)}
                </Tag>
            ) : null
        },
        {
            title: t("fields", "Fields"),
            dataIndex: "custom_meta_fields",
            render: fields => (
                <Space wrap size={[4, 4]}>
                    {fields?.map(membership => (
                        <Tag key={membership.custom_meta_field.id}>{membership.custom_meta_field.name}</Tag>
                    ))}
                </Space>
            )
        },
        {
            title: t("enabled", "Enabled"),
            dataIndex: "enabled",
            width: 110,
            filters: [
                {text: t("yes", "Yes"), value: true},
                {text: t("no", "No"), value: false}
            ],
            onFilter: (value, record) => !!record.enabled === value,
            sorter: (a, b) => Number(!!a.enabled) - Number(!!b.enabled),
            render: enabled => enabled ? t("yes", "Yes") : t("no", "No")
        },
        actionColumn("group")
    ];

    function actionColumn(kind) {
        return {
            title: "",
            key: "actions",
            width: kind === "entityType" ? 170 : kind === "metadataEntityType" ? 170 : 120,
            render: (_, record) => (
                <Space>
                    {kind === "entityType" && (
                        <Button size="small" icon={<PlusOutlined/>} onClick={() => openAssetCreator(record)}>
                            {t("create", "Create")}
                        </Button>
                    )}
                    {kind === "metadataEntityType" && !record.standard_tag_schema && (
                        <Tooltip title={t("convert-cmf", "Convert CMF")}>
                            <Button size="small" icon={<SwapOutlined/>} onClick={() => openConversionModal(record)}/>
                        </Tooltip>
                    )}
                    {kind === "metadataEntityType" && record.id && <MetadataEntitySchemaOrder type={record} onChanged={loadData}/>}
                    {kind === "metadataEntityType" && record.standard_tag_schema && canImportStandardSchema(record) && <MetadataEntityTypeCreator fields={assetFields} standardSchema={record} organizationId={currentOrg?.id} onCreated={loadData}/>}
                    {kind === "metadataEntityType" && record.id && (
                        <Tooltip title={t("export-csv", "Export CSV")}>
                            <Button size="small" icon={<DownloadOutlined/>} onClick={() => exportMetadataEntityType(record)}/>
                        </Tooltip>
                    )}
                    <Button size="small" icon={<EditOutlined/>} onClick={() => openEditor(kind, record)}/>
                    {!record.standard_tag_schema && (
                        <Popconfirm
                            title={t("confirm-remove-configuration", "Remove this configuration?")}
                            onConfirm={() => destroyRecord(kind, record)}
                        >
                            <Button size="small" danger icon={<DeleteOutlined/>}/>
                        </Popconfirm>
                    )}
                </Space>
            )
        };
    }

    const tabExtra = kind => (
        <Button type="primary" icon={<PlusOutlined/>} onClick={() => openEditor(kind)}>
            {t("new", "New")}
        </Button>
    );

    return (
        <>
            <Tabs
                activeKey={tab}
                onTabClick={onTabClick}
                items={[
                    !metadataMode && {
                        key: "text-asset-types",
                        label: <Space><BookOutlined/>{t("configurable-text-assets", "Text Asset Types")}</Space>,
                        children: (
                            <TextAssetTypes types={entityTypes} fields={assetFields} canCreate={canEditTypes} loading={loading} onChanged={loadData} onCreateAsset={openAssetCreator}/>
                        )
                    },
                    metadataMode && metadataEntitiesEnabled && canManageMetadataEntityTypes && {
                        key: "metadata-entities",
                        label: t("metadata-entities", "Metadata Entities"),
                        children: <Space direction="vertical" style={{width: "100%"}} size="large">
                            <Typography.Title level={4}>{t("standard-metadata-entities", "Standard Entities")}</Typography.Title>
                            <ConfigTable data={standardTagSchemas} loading={loading} columns={schemaColumns("metadataEntityType")} rowKey="standard_tag_sub_type" scrollX={1600}/>
                            <Typography.Title level={4}>{t("custom-metadata-entities", "Custom Entities")}</Typography.Title>
                            <ConfigTable data={customMetadataEntityTypes} loading={loading} columns={schemaColumns("metadataEntityType")}
                                extra={<MetadataEntityTypeCreator fields={assetFields} organizationId={currentOrg?.id} onCreated={loadData}/>} scrollX={1600}/>
                        </Space>
                    },
                    metadataMode && metadataEntitiesEnabled && canManageCustomMetaFields && {
                        key: "metadata-entity-fields",
                        label: t("metadata-entity-fields", "Metadata Entity Fields"),
                        children: <Space direction="vertical" style={{width: "100%"}} size="large">
                            <CustomMetaFields embedded ownerType="MetadataEntity" typeTitle={t("metadata-entity", "Metadata Entity")}/>
                            {canManageCustomMetaGroups && <>
                                <Typography.Title level={4}>{t("field-groups", "Field Groups")}</Typography.Title>
                                <ConfigTable data={groups} loading={loading} columns={groupColumns} extra={tabExtra("group")}/>
                            </>}
                        </Space>
                    },
                    !metadataMode && canManageCustomMetaGroups && {
                        key: "field-groups",
                        label: t("asset-field-groups", "Asset Field Groups"),
                        children: <ConfigTable data={groups.filter(group => (group.custom_meta_fields || []).every(entry => entry.custom_meta_field?.owner_type === "Asset"))} loading={loading} columns={groupColumns} extra={tabExtra("group")}/>
                    },
                    !metadataMode && {
                        key: "text-asset-imports",
                        label: <Space><UploadOutlined/>{t("text-asset-imports", "Text Asset Imports")}</Space>,
                        children: (
                            <Space direction="vertical" style={{width: "100%"}} size="middle">
                                <Table
                                    rowKey="id"
                                    size="middle"
                                    loading={loading}
                                    dataSource={assetImports}
                                    columns={assetImportColumns(t, loadData, entityTypes)}
                                    pagination={false}
                                />
                            </Space>
                        )
                    },
                    metadataMode && metadataEntitiesEnabled && canManageMetadataEntityImports && {
                        key: "metadata-entity-imports",
                        label: <Space><UploadOutlined/>{t("metadata-entity-imports", "Metadata Entity Imports")}</Space>,
                        children: (
                            <Space direction="vertical" style={{width: "100%"}} size="middle">
                                <div style={{display: "flex", justifyContent: "flex-end"}}>
                                    <NewMetadataEntityImportButton
                                        metadataEntityTypes={importableMetadataEntityTypes}
                                        onCreate={loadData}
                                        onDone={loadData}
                                    />
                                </div>
                                <Table
                                    rowKey="id"
                                    size="middle"
                                    loading={loading}
                                    dataSource={metadataEntityImports}
                                    columns={metadataEntityImportColumns(t, loadData, metadataEntityTypes)}
                                    pagination={false}
                                />
                            </Space>
                        )
                    }
                ].filter(Boolean)}
            />

            <Modal
                open={modalOpen}
                title={modalTitle(editing, t)}
                onCancel={closeEditor}
                onOk={saveEditor}
                okButtonProps={{loading: savingEditor}}
                destroyOnClose
                width={720}
            >
                <Form form={form} layout="vertical" preserve={false}>
                    <Form.Item name="name" label={t("name", "Name")} rules={[{required: true}]}>
                        <Input disabled={!!editing?.record?.standard_tag_sub_type}/>
                    </Form.Item>
                    {editing?.kind !== "group" && (
                        <Form.Item name="slug" label={t("slug", "Slug")}>
                            <Input disabled={!!editing?.record?.standard_tag_sub_type}/>
                        </Form.Item>
                    )}
                    <Form.Item
                        name="icon"
                        label={t("icon", "Icon")}
                        normalize={value => value?.trim()}
                        extra={t("icon-emoji-hint", "Choose from the list, or type a single character/emoji (e.g. 📋).")}
                    >
                        <AutoComplete
                            allowClear
                            disabled={!!editing?.record?.standard_tag_sub_type}
                            options={configurableIconSelectOptions()}
                            placeholder={t("icon-placeholder", "Choose an icon or type an emoji")}
                            filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())}
                        />
                    </Form.Item>
                    <Form.Item name="description" label={t("description", "Description")}>
                        <Input.TextArea rows={3}/>
                    </Form.Item>
                    <Form.Item name="enabled" label={t("enabled", "Enabled")} valuePropName="checked">
                        <Switch/>
                    </Form.Item>
                    {editing?.kind === "entityType" && (
                        <Form.Item
                            name="main_text_editable"
                            label={t("main-text-editable", "Main Text editable")}
                            valuePropName="checked"
                            extra={t("main-text-editable-help", "When disabled, Main Text remains visible but cannot be edited after the asset is created.")}
                        >
                            <Switch/>
                        </Form.Item>
                    )}

                    {editing?.kind !== "group" && (
                        <Form.Item name="group_ids" label={t("field-groups", "Field Groups")}>
                            <Select mode="multiple" options={groupOptions} showSearch optionFilterProp="label"/>
                        </Form.Item>
                    )}

                    {editing?.kind === "metadataEntityType" && !editing?.record?.standard_tag_sub_type && (
                        <Form.Item
                            name="standard_field_keys"
                            label={t("standard-metadata-fields", "Standard Metadata Fields")}
                        >
                            <Select
                                mode="multiple"
                                options={STANDARD_METADATA_FIELD_OPTIONS}
                                showSearch
                                optionFilterProp="label"
                            />
                        </Form.Item>
                    )}

                    <Form.Item name="field_ids" label={editing?.kind === "group" ? t("fields", "Fields") : t("ungrouped-fields", "Ungrouped Fields")}>
                        <Select mode="multiple" options={fieldOptions} showSearch optionFilterProp="label"/>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                open={assetModalOpen}
                title={creatingEntityType && t("create-entity-asset", "Create {{name}}", {name: creatingEntityType.name})}
                onCancel={closeAssetCreator}
                onOk={saveAssetCreator}
                okText={t("create", "Create")}
                okButtonProps={{disabled: !assetFormSubmittable || creatingAsset, loading: creatingAsset}}
                destroyOnClose
                width={760}
            >
                <Form form={assetForm} layout="vertical" preserve={false} onValuesChange={updateAssetCreatorSubmittable}>
                    {(!creatingEntityType?.display_config?.filename_source || creatingEntityType.display_config.filename_source === 'input') && <Form.Item name="filename" label={t("filename-label", "Filename / label")} rules={[{required: true, whitespace: true}]}>
                        <Input/>
                    </Form.Item>}
                    {(!creatingEntityType?.display_config?.main_text_source || creatingEntityType.display_config.main_text_source === 'input') && <Form.Item name="content" label={t("main-text", "Main Text")}>
                        <Input.TextArea rows={4}/>
                    </Form.Item>}
                    <EntityAssetFormFields entityType={creatingEntityType}/>
                </Form>
            </Modal>

            <Modal
                open={conversionModalOpen}
                title={convertingMetadataEntityType && t("convert-cmf-to-cme", "Convert CMF to {{name}}", {name: convertingMetadataEntityType.name})}
                onCancel={closeConversionModal}
                onOk={convertCustomMetaField}
                okText={t("convert", "Convert")}
                okButtonProps={{loading: converting}}
                destroyOnClose
                width={760}
            >
                <Typography.Paragraph>
                    {t("cmf-conversion-help", "Each distinct field value becomes a Custom Metadata Entity and remains attached to the same assets through tags. The source field and values are retained for audit and rollback.")}
                </Typography.Paragraph>
                <Form form={conversionForm} layout="vertical" preserve={false}>
                    <Form.Item
                        name="custom_meta_field_id"
                        label={t("asset-custom-metadata-field", "Asset custom metadata field")}
                        rules={[{required: true}]}
                    >
                        <Select
                            showSearch
                            optionFilterProp="label"
                            options={conversionFields.map(field => ({value: field.id, label: field.name}))}
                        />
                    </Form.Item>
                </Form>
                {!!convertingMetadataEntityType?.cmf_conversions?.length && (
                    <Table
                        rowKey="id"
                        size="small"
                        pagination={false}
                        dataSource={convertingMetadataEntityType.cmf_conversions}
                        columns={[
                            {title: t("field", "Field"), dataIndex: "custom_meta_field_name"},
                            {title: t("values", "Values"), dataIndex: "value_count", width: 90},
                            {title: t("associations", "Associations"), dataIndex: "tagging_count", width: 110},
                            {
                                title: t("status", "Status"),
                                dataIndex: "status",
                                width: 110,
                                render: (status, conversion) => (
                                    <Tooltip title={conversion.error}>
                                        <Tag color={status === "active" ? "green" : status === "failed" ? "red" : status === "pending" ? "blue" : "default"}>
                                            {status}
                                        </Tag>
                                    </Tooltip>
                                )
                            },
                            {
                                title: "",
                                width: 120,
                                render: (_, conversion) => conversion.status === "active" && (
                                    <Popconfirm
                                        title={t("confirm-rollback-cmf-conversion", "Remove the converted tag associations and restore the source-only state?")}
                                        onConfirm={() => rollbackCustomMetaFieldConversion(conversion)}
                                    >
                                        <Button size="small" icon={<RollbackOutlined/>}>{t("rollback", "Rollback")}</Button>
                                    </Popconfirm>
                                )
                            }
                        ]}
                    />
                )}
            </Modal>
        </>
    );
}

function SchemaFieldList({description, fields}) {
    const {t} = useTranslation();

    return (
        <Space direction="vertical" size={4} style={{maxWidth: 320}}>
            {description && <Text type="secondary">{description}</Text>}
            {fields.length ? fields.map(field => (
                <div key={field.id}>
                    {field.name}{" "}
                    <Text type="secondary">
                        ({field.value_type}{field.free ? "" : `, ${t("controlled", "controlled")}`}{field.multi ? `, ${t("multi", "multi")}` : ""})
                    </Text>
                </div>
            )) : <Text type="secondary">{t("no-fields", "No fields")}</Text>}
        </Space>
    );
}

function GroupTag({membership}) {
    const group = membership.custom_meta_group;
    if (!group) return null;

    const fields = (group.custom_meta_fields || [])
        .map(fieldMembership => fieldMembership.custom_meta_field)
        .filter(Boolean);

    return (
        <Popover
            title={<>{group.icon && <ConfigurableEntityIcon icon={group.icon}/>}{group.name}</>}
            content={<SchemaFieldList description={group.description} fields={fields}/>}
        >
            <Tag style={{cursor: "default"}}>
                {group.icon && <ConfigurableEntityIcon icon={group.icon}/>}
                {group.name}
            </Tag>
        </Popover>
    );
}

function ConfigurableTypeTag({type, kind}) {
    if (!type) return null;

    const fields = kind === "metadata_entity" ? metadataEntityTypeFields(type) : entityTypeFields(type);

    return (
        <Popover
            title={<><ConfigurableEntityIcon icon={type.icon} kind={kind}/>{type.name}</>}
            content={<SchemaFieldList description={type.description} fields={fields}/>}
        >
            <Tag style={{cursor: "default"}}>
                <ConfigurableEntityIcon icon={type.icon} kind={kind}/>
                {type.name}
            </Tag>
        </Popover>
    );
}

function ConfigTable({data, loading, columns, extra, rowKey = "id", scrollX}) {
    return (
        <Space direction="vertical" style={{width: "100%"}} size="middle">
            <div style={{display: "flex", justifyContent: "flex-end"}}>{extra}</div>
            <Table
                rowKey={rowKey}
                size="middle"
                loading={loading}
                dataSource={data}
                columns={columns}
                pagination={false}
                scroll={scrollX ? {x: scrollX} : undefined}
            />
        </Space>
    );
}

function ImportStateTag({state}) {
    const {t} = useTranslation();

    switch (state) {
        case "pending":
            return <Tag icon={<ClockCircleOutlined/>}>{t("pending", "Pending")}</Tag>;
        case "analyzing":
            return <Tag color="blue" icon={<LoadingOutlined/>}>{t("analyzing", "Analyzing")}</Tag>;
        case "analyzed":
            return <Tag color="gold" icon={<FileSearchOutlined/>}>{t("analyzed", "Analyzed")}</Tag>;
        case "processing":
            return <Tag color="blue" icon={<LoadingOutlined/>}>{t("processing", "Processing")}</Tag>;
        case "processed":
            return <Tag color="green" icon={<CheckCircleOutlined/>}>{t("processed", "Processed")}</Tag>;
        case "errored":
            return <Tag color="red" icon={<AlertOutlined/>}>{t("errored", "Errored")}</Tag>;
        default:
            return <Tag>{state}</Tag>;
    }
}

function NewAssetImportButton({entityTypes, onCreate, onDone}) {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const [entityTypeId, setEntityTypeId] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [assetImport, setAssetImport] = useState(null);

    const onClick = () => {
        setAssetImport(null);
        setEntityTypeId(null);
        setOpen(true);
    };

    const close = () => {
        setOpen(false);
        setAssetImport(null);
    };

    const onAddFile = (file, storageFolderId) => {
        const formData = new FormData();
        formData.append("asset_spreadsheet_import[entity_type_id]", entityTypeId);
        if (storageFolderId) formData.append("asset_spreadsheet_import[storage_folder_id]", storageFolderId);
        formData.append("asset_spreadsheet_import[file]", file);

        setUploading(true);
        api.post(typeConfig.assetImport.endpoint, formData).then(res => {
            setUploading(false);
            setAssetImport(res.data);
            onCreate && onCreate(res.data);
        }).catch(error => {
            setUploading(false);
            handleApiError(error);
        });

        return false;
    };

    return (<>
        <Button type="primary" icon={<PlusOutlined/>} onClick={onClick}>
            {t("button-new-import", "New Import")}
        </Button>
        <Modal
            title={t("new-text-asset-import", "New Text Asset Import")}
            open={open}
            onCancel={close}
            footer={null}
            destroyOnClose
        >
            {assetImport ? (
                <AssetSpreadsheetImportSetup
                    assetImport={assetImport}
                    onDone={() => {
                        close();
                        onDone && onDone();
                    }}
                />
            ) : (
                <Formik
                    initialValues={{storage_folder_id: null}}
                    enableReinitialize
                    onSubmit={() => {}}
                >
                    {({values, setFieldValue}) => (
                        <Space direction="vertical" style={{width: "100%"}}>
                            <Select
                                style={{width: "100%"}}
                                placeholder={t("choose-text-asset-type", "Choose text asset type")}
                                value={entityTypeId}
                                onChange={setEntityTypeId}
                                options={entityTypes.map(entityType => ({value: entityType.id, label: entityType.name}))}
                            />
                            <div>
                                <Space style={{width: "100%", justifyContent: "space-between"}}>
                                    <Text strong>{t("choose-import-target-folder-optional", "Target folder (optional)")}</Text>
                                    <Button
                                        type="link"
                                        size="small"
                                        disabled={!values.storage_folder_id}
                                        onClick={() => setFieldValue("storage_folder_id", null)}
                                    >
                                        {t("clear", "Clear")}
                                    </Button>
                                </Space>
                                <div style={{border: "1px solid #d9d9d9", borderRadius: 6, padding: 12, maxHeight: 320, overflow: "auto"}}>
                                    <AssetGroupChooser
                                        type="storage_folders"
                                        fieldName="storage_folder_id"
                                        sessionKey="asset-spreadsheet-import-target-folder"
                                        draggable={false}
                                    />
                                </div>
                            </div>
                            <Upload beforeUpload={file => onAddFile(file, values.storage_folder_id)} showUploadList={false} accept=".csv,.tsv,.xls,.xlsx,.xml">
                                <Button icon={<UploadOutlined/>} loading={uploading} disabled={!entityTypeId} block>
                                    {uploading ? t("uploading", "Uploading...") : t("select-file", "Select File...")}
                                </Button>
                            </Upload>
                            <Text type="secondary"><em>{t("asset-import-supported-filetypes", "Supported file types: .csv, .tsv, .xls, .xlsx, .xml")}</em></Text>
                        </Space>
                    )}
                </Formik>
            )}
        </Modal>
    </>);
}

function AssetImportActions({assetImport, onChanged}) {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);

    const destroy = () => {
        api.delete(`${typeConfig.assetImport.endpoint}/${assetImport.id}`).then(() => {
            message.success(t("asset-import-canceled", "Import canceled."));
            onChanged && onChanged();
        }).catch(handleApiError);
    };

    return (
        <Space size={5}>
            {["analyzed", "processed", "errored"].includes(assetImport.aasm_state) && (<>
                <Button size="small" icon={<SettingOutlined/>} onClick={() => setOpen(true)}>
                    {t("button-process", "Process")}...
                </Button>
                <Modal
                    title={t("process-text-asset-import", "Process Text Asset Import")}
                    width={1100}
                    open={open}
                    onCancel={() => setOpen(false)}
                    footer={null}
                    destroyOnClose
                >
                    <AssetSpreadsheetImportSetup
                        assetImport={assetImport}
                        onDone={() => {
                            setOpen(false);
                            onChanged && onChanged();
                        }}
                    />
                </Modal>
            </>)}
            {assetImport.download_url && (
                <Tooltip title={t("download-uploaded-file", "Download uploaded file")}>
                    <Button
                        size="small"
                        icon={<DownloadOutlined/>}
                        href={assetImport.download_url}
                        download={assetImport.filename}
                        target="_blank"
                        rel="noopener noreferrer"
                    />
                </Tooltip>
            )}
            {!["processing", "processed"].includes(assetImport.aasm_state) && (
                <Popconfirm
                    title={t("confirm-cancel-import", "Cancel this import?")}
                    onConfirm={destroy}
                >
                    <Button size="small" danger icon={<DeleteOutlined/>}/>
                </Popconfirm>
            )}
        </Space>
    );
}

function MappedColumnsCell({record, labelFor}) {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const columns = record.columns || [];
    const mappedCount = columns.filter(column => column.mapping).length;

    if (!columns.length) return <Text type="secondary">0/0</Text>;

    return (<>
        <Button type="link" size="small" style={{padding: 0}} onClick={() => setOpen(true)}>
            {mappedCount}/{columns.length}
        </Button>
        <Modal
            title={`${t("column-mappings", "Column Mappings")}: ${record.filename}`}
            open={open}
            onCancel={() => setOpen(false)}
            footer={null}
            destroyOnClose
        >
            <Table
                rowKey={column => `${record.id}-${column.index}-${column.name}`}
                size="small"
                dataSource={columns}
                pagination={false}
                columns={[
                    {
                        title: t("source-column", "Source Column"),
                        dataIndex: "name"
                    },
                    {
                        title: t("mapping", "Mapping"),
                        render: (_, column) => column.mapping ?
                            labelFor(column, t) :
                            <Text type="secondary">{labelFor(column, t)}</Text>
                    }
                ]}
            />
        </Modal>
    </>);
}

function assetImportColumns(t, onChanged, entityTypes) {
    return [
        {
            title: t("file", "File"),
            dataIndex: "filename",
            render: filename => <Text strong>{filename}</Text>
        },
        {
            title: t("type", "Type"),
            dataIndex: "entity_type_id",
            width: 180,
            filters: (entityTypes || []).map(entityType => ({text: entityType.name, value: entityType.id})),
            onFilter: (value, record) => record.entity_type_id === value,
            render: (_, record) => <ConfigurableTypeTag type={record.entity_type} kind="text_asset"/>
        },
        {
            title: t("target-folder", "Target Folder"),
            dataIndex: "storage_folder",
            width: 180,
            render: folder => folder?.path_names?.join(" / ") || folder?.name || null
        },
        {
            title: t("created", "Created"),
            dataIndex: "created_at",
            width: 140,
            render: date => <TimeAgo date={date}/>
        },
        {
            title: t("created-by", "Created By"),
            dataIndex: "user",
            width: 160,
            render: user => user && <User user={user}/>
        },
        {
            title: t("rows", "Rows"),
            dataIndex: "rows",
            width: 90
        },
        {
            title: t("state", "State"),
            dataIndex: "aasm_state",
            width: 120,
            render: state => <ImportStateTag state={state}/>
        },
        {
            title: t("mapped-columns", "Mapped Columns"),
            key: "mapped-columns",
            width: 150,
            render: (_, record) => (
                <MappedColumnsCell
                    record={record}
                    labelFor={(column, tFn) => mappingLabel(record.entity_type, column, tFn)}
                />
            )
        },
        {
            title: t("assets", "Assets"),
            dataIndex: "assets_count",
            width: 90,
            render: (assetsCount, record) => (
                <OrgNavLink to={`/explore?asset_spreadsheet_import_id=${record.id}`}>
                    {assetsCount || 0}
                </OrgNavLink>
            )
        },
        {
            title: "",
            key: "actions",
            width: 200,
            render: (_, record) => <AssetImportActions assetImport={record} onChanged={onChanged}/>
        }
    ];
}

function NewMetadataEntityImportButton({metadataEntityTypes, onCreate, onDone}) {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const [metadataEntityTypeId, setMetadataEntityTypeId] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [metadataImport, setMetadataImport] = useState(null);

    const onClick = () => {
        setMetadataImport(null);
        setMetadataEntityTypeId(null);
        setOpen(true);
    };

    const close = () => {
        setOpen(false);
        setMetadataImport(null);
    };

    const onAddFile = file => {
        const formData = new FormData();
        formData.append("metadata_entity_import[metadata_entity_type_id]", metadataEntityTypeId);
        formData.append("metadata_entity_import[file]", file);

        setUploading(true);
        api.post(typeConfig.metadataImport.endpoint, formData).then(res => {
            setUploading(false);
            setMetadataImport(res.data);
            onCreate && onCreate(res.data);
        }).catch(error => {
            setUploading(false);
            handleApiError(error);
        });

        return false;
    };

    return (<>
        <Button type="primary" icon={<PlusOutlined/>} onClick={onClick}>
            {t("button-new-import", "New Import")}
        </Button>
        <Modal
            title={t("new-custom-metadata-entity-import", "New Custom Metadata Entity Import")}
            open={open}
            onCancel={close}
            footer={null}
            destroyOnClose
        >
            {metadataImport ? (
                <MetadataEntityImportSetup
                    metadataImport={metadataImport}
                    onDone={() => {
                        onDone && onDone();
                    }}
                />
            ) : (
                <Space direction="vertical" style={{width: "100%"}}>
                    <Select
                        style={{width: "100%"}}
                        placeholder={t("choose-custom-metadata-entity-type", "Choose custom metadata entity type")}
                        value={metadataEntityTypeId}
                        onChange={setMetadataEntityTypeId}
                        options={metadataEntityTypes.map(metadataEntityType => ({value: metadataEntityType.id, label: metadataEntityType.name}))}
                    />
                    <Upload beforeUpload={onAddFile} showUploadList={false} accept=".csv,.tsv,.xls,.xlsx">
                        <Button icon={<UploadOutlined/>} loading={uploading} disabled={!metadataEntityTypeId} block>
                            {uploading ? t("uploading", "Uploading...") : t("select-file", "Select File...")}
                        </Button>
                    </Upload>
                    <Text type="secondary"><em>{t("metadata-import-supported-filetypes", "Supported file types: .csv, .tsv, .xls, .xlsx")}</em></Text>
                </Space>
            )}
        </Modal>
    </>);
}

function metadataEntityImportColumns(t, onChanged, metadataEntityTypes) {
    return [
        {
            title: t("file", "File"),
            dataIndex: "filename",
            render: filename => <Text strong>{filename}</Text>
        },
        {
            title: t("type", "Type"),
            dataIndex: "metadata_entity_type_id",
            width: 180,
            filters: (metadataEntityTypes || []).map(metadataEntityType => ({text: metadataEntityType.name, value: metadataEntityType.id})),
            onFilter: (value, record) => record.metadata_entity_type_id === value,
            render: (_, record) => <ConfigurableTypeTag type={record.metadata_entity_type} kind="metadata_entity"/>
        },
        {
            title: t("created", "Created"),
            dataIndex: "created_at",
            width: 140,
            render: date => <TimeAgo date={date}/>
        },
        {
            title: t("created-by", "Created By"),
            dataIndex: "user",
            width: 160,
            render: user => user && <User user={user}/>
        },
        {title: t("rows", "Rows"), dataIndex: "rows", width: 90},
        {
            title: t("state", "State"),
            dataIndex: "aasm_state",
            width: 120,
            render: state => <ImportStateTag state={state}/>
        },
        {
            title: t("mapped-columns", "Mapped Columns"),
            key: "mapped-columns",
            width: 150,
            render: (_, record) => (
                <MappedColumnsCell
                    record={record}
                    labelFor={(column, tFn) => metadataEntityMappingLabel(record.metadata_entity_type, column, tFn)}
                />
            )
        },
        {
            title: t("entities", "Entities"),
            dataIndex: "entities_count",
            width: 100,
            render: (entitiesCount, record) => (
                <Tooltip
                    title={`${t("created", "Created")}: ${record.created_count || 0} · ${t("updated", "Updated")}: ${record.updated_count || 0} · ${t("skipped", "Skipped")}: ${record.skipped_count || 0}`}
                >
                    <span>
                        <OrgNavLink to={`/manage/taxonomy#${record.metadata_entity_type?.standard_tag_sub_type ? STANDARD_TAG_TAXONOMY_HASHES[record.metadata_entity_type.standard_tag_sub_type] : `browse-metadata-entity-${record.metadata_entity_type_id}`}`}>
                            {entitiesCount || 0}
                        </OrgNavLink>
                    </span>
                </Tooltip>
            )
        },
        {
            title: "",
            key: "actions",
            width: 160,
            render: (_, record) => <MetadataEntityImportActions metadataImport={record} onChanged={onChanged}/>
        }
    ];
}

function nestedMemberships(existingMemberships, selectedIds, targetKey, getTargetId) {
    const selected = selectedIds.map(id => parseInt(id, 10));
    const existingByTargetId = new Map(existingMemberships.map(membership => [getTargetId(membership), membership]));
    const selectedAttributes = selected.map((id, index) => {
        const existing = existingByTargetId.get(id);
        return {
            ...(existing?.id ? {id: existing.id} : {}),
            [targetKey]: id,
            position: index + 1
        };
    });

    const removedAttributes = existingMemberships
        .filter(membership => !selected.includes(getTargetId(membership)))
        .map(membership => ({
            id: membership.id,
            [targetKey]: getTargetId(membership),
            _destroy: true
        }));

    return [...selectedAttributes, ...removedAttributes];
}

function modalTitle(editing, t) {
    if (!editing) return "";
    const prefix = editing.record ? t("edit", "Edit") : t("new", "New");
    return `${prefix} ${t(typeConfig[editing.kind].titleKey, typeConfig[editing.kind].title)}`;
}
